import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import tippy from 'tippy.js'

// Block Menu Extension for Notion-like block operations
export const BlockMenuExtension = Extension.create({
  name: 'blockMenu',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('blockMenu'),
        view: (editorView) => {
          return new BlockMenuView(editorView, this.editor)
        },
      }),
    ]
  },
})

class BlockMenuView {
  constructor(view, editor) {
    this.view = view
    this.editor = editor
    this.menuButton = null
    this.currentPos = null
    this.popup = null

    // 建立選單按鈕
    this.createMenuButton()

    // 監聽編輯器更新
    this.view.dom.addEventListener('mouseover', this.handleMouseOver.bind(this))
  }

  createMenuButton() {
    this.menuButton = document.createElement('button')
    this.menuButton.className = 'block-menu-handle'
    this.menuButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 2 L8 14 M2 8 L14 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `
    this.menuButton.type = 'button'
    this.menuButton.style.display = 'none'
    this.menuButton.addEventListener('click', this.handleMenuButtonClick.bind(this))

    // 加到 body
    document.body.appendChild(this.menuButton)
  }

  handleMouseOver(event) {
    const target = event.target
    const block = this.findBlockElement(target)

    if (!block) {
      this.hideMenuButton()
      return
    }

    // 顯示選單按鈕
    this.showMenuButton(block)
  }

  findBlockElement(element) {
    // 找到 ProseMirror block 元素
    let current = element

    while (current && current !== this.view.dom) {
      if (current.matches && current.matches('p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, pre, .tableWrapper')) {
        return current
      }
      current = current.parentElement
    }

    return null
  }

  showMenuButton(block) {
    const rect = block.getBoundingClientRect()
    const editorRect = this.view.dom.getBoundingClientRect()

    // 計算按鈕位置（editor 左側 30px 處）
    // 使用 fixed positioning 相對於 viewport
    const leftPos = Math.max(10, editorRect.left - 30)
    const topPos = rect.top + (rect.height / 2) - 12

    // 定位到 block 左側 (使用 fixed positioning 相對於 viewport)
    this.menuButton.style.display = 'flex'
    this.menuButton.style.position = 'fixed'
    this.menuButton.style.left = `${leftPos}px`
    this.menuButton.style.top = `${topPos}px`
    this.menuButton.style.zIndex = '9999'

    // 儲存當前位置
    this.currentBlock = block
  }

  hideMenuButton() {
    this.menuButton.style.display = 'none'
  }

  handleMenuButtonClick(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.currentBlock) return

    // 建立選單
    const menuContent = this.createMenu()

    // 使用 tippy 顯示選單
    if (this.popup) {
      this.popup.destroy()
    }

    this.popup = tippy(this.menuButton, {
      content: menuContent,
      placement: 'right-start',
      theme: 'block-menu',
      interactive: true,
      trigger: 'manual',
      showOnCreate: true,
      onHidden: () => {
        this.popup = null
      },
    })
  }

  createMenu() {
    const menu = document.createElement('div')
    menu.className = 'block-menu-dropdown'

    // 取得當前編輯器的模式（html 或 markdown）
    const mode = this.editor?.storage?.slashMenu?.mode || 'html'

    // 取得 commands（跟 slash menu 一樣）
    const commands = getBlockCommands(mode)

    menu.innerHTML = commands.map(item => {
      return `
        <button type="button" class="block-menu-item" data-action="${item.title}">
          <span class="block-menu-item-icon">${item.icon}</span>
          <div class="block-menu-item-content">
            <div class="block-menu-item-title">${item.title}</div>
            <div class="block-menu-item-description">${item.description}</div>
          </div>
        </button>
      `
    }).join('')

    // 綁定事件
    menu.querySelectorAll('.block-menu-item').forEach((btn, index) => {
      btn.addEventListener('click', () => {
        const item = commands[index]
        if (item && item.command) {
          // 執行 command（在當前 block 之後插入）
          this.insertBlockWithCommand(item.command)
          if (this.popup) {
            this.popup.hide()
          }
        }
      })
    })

    return menu
  }

  // Block 操作方法
  // 取得 editor 實例
  getEditor() {
    return this.editor
  }

  // 取得當前 block 在文檔中的位置
  getBlockPos() {
    const domRect = this.currentBlock.getBoundingClientRect()
    const pos = this.view.posAtCoords({
      left: domRect.left,
      top: domRect.top + 1,
    })
    return pos ? pos.pos : null
  }

  // 使用 command 在當前 block 之後插入新的 block
  insertBlockWithCommand(command) {
    const editor = this.getEditor()
    if (!editor) return

    const pos = this.getBlockPos()
    if (pos === null) return

    const { state } = this.view
    const $pos = state.doc.resolve(pos)

    // 找到當前 node 的結束位置
    const nodeEnd = $pos.after()

    // 執行 command（參數格式跟 slash menu 一致）
    command({
      editor,
      range: { from: nodeEnd, to: nodeEnd },
    })
  }

  destroy() {
    if (this.menuButton) {
      this.menuButton.remove()
    }
    if (this.popup) {
      this.popup.destroy()
    }
  }
}

// Commands for block insertion (same as slash menu)
function getBlockCommands(mode) {
  const htmlCommands = [
    {
      title: 'Heading 1 (H1)',
      description: '大標題',
      icon: 'H1',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'heading', attrs: { level: 1 }, content: [] })
          .run()
      }
    },
    {
      title: 'Heading 2 (H2)',
      description: '中標題',
      icon: 'H2',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'heading', attrs: { level: 2 }, content: [] })
          .run()
      }
    },
    {
      title: 'Heading 3 (H3)',
      description: '小標題',
      icon: 'H3',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'heading', attrs: { level: 3 }, content: [] })
          .run()
      }
    },
    {
      title: 'Heading 4 (H4)',
      description: '次標題',
      icon: 'H4',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'heading', attrs: { level: 4 }, content: [] })
          .run()
      }
    },
    {
      title: 'Paragraph',
      description: '一般段落',
      icon: '¶',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'paragraph', content: [] })
          .run()
      }
    },
    {
      title: 'Bullet List',
      description: '無序列表',
      icon: '•',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'bulletList', content: [{ type: 'listItem', content: [{ type: 'paragraph' }] }] })
          .run()
      }
    },
    {
      title: 'Numbered List',
      description: '有序列表',
      icon: '1.',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'orderedList', content: [{ type: 'listItem', content: [{ type: 'paragraph' }] }] })
          .run()
      }
    },
    {
      title: 'Task List',
      description: '待辦事項',
      icon: '☑',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'taskList', content: [{ type: 'taskItem', content: [{ type: 'paragraph' }] }] })
          .run()
      }
    },
    {
      title: 'Blockquote',
      description: '引用區塊',
      icon: '❝',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'blockquote', content: [{ type: 'paragraph' }] })
          .run()
      }
    },
    {
      title: 'Code Block',
      description: '程式碼區塊',
      icon: '</>',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'codeBlock', content: [] })
          .run()
      }
    },
    {
      title: 'Horizontal Rule',
      description: '分隔線',
      icon: '—',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .insertContentAt(range.to, { type: 'horizontalRule' })
          .run()
      }
    },
  ]

  // HTML 模式才有的指令
  if (mode === 'html') {
    htmlCommands.push(
      {
        title: 'Table',
        description: '插入表格',
        icon: '⊞',
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .insertContentAt(range.to, { type: 'paragraph' })
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
      }
    )
  }

  const markdownCommands = htmlCommands.filter(cmd => {
    // Markdown 模式排除 Table
    return cmd.title !== 'Table'
  })

  return mode === 'html' ? htmlCommands : markdownCommands
}
